import { MapsAPILoader } from '@agm/core';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
    GeneralInformationWAMessageValidationConst,
    MediaTypeOptionConst,
    MsgTypeWAOptionConst,
} from '../../../../../core/const';
import { ProductCategoryEnum } from '../../../../../core/enums';
import { BaseOptionModel } from '../../../../../core/models';
import { Parameter } from '../../../../../core/models/util/api-adapter-service-models';
import { AppResponse } from '../../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../../core/services';
import { generateEnumOption } from '../../../../../core/util';
import { TemplateWAModel, WhatsAppAccountDto } from '../../../../models';
import { EnumData } from '../../../../util/enums';
import { Validators as CustomValidators } from '../../../../validators';

interface IMaps {
    zoom: number;
    latitude: number | null;
    longitude: number | null;
    isDraggable: boolean;
    isDoubleClickZoom: boolean;
}

@Component({
    selector: 'lib-general-information-wa',
    templateUrl: './general-information-wa.component.html',
    styleUrls: ['./general-information-wa.component.scss'],
})
export class GeneralInformationWAComponent implements OnInit, OnDestroy {
    @Output()
    public updateGeneralInformationForm: EventEmitter<FormGroup>;

    public form!: FormGroup;
    public msgTypeWAOption: any[] = [];
    public mediaTypeOption: any[] = [];
    public categoryOption!: BaseOptionModel[];
    public accountOption: WhatsAppAccountDto[] = [];
    public isHaveNextAccountOption: boolean = true;
    public isLoadingAccountOption: boolean = false;
    public templateOption: TemplateWAModel[] = [];
    public isHaveNextTemplateOption: boolean = true;
    public isLoadingTemplateOption: boolean = false;
    public isHaveTemplateParam: boolean = false;
    public isShowParameter: boolean = false;
    public validationMessages: any;
    public extFileAccepted: string = 'image/*';
    public maps!: IMaps;

    private pageNoAccountOption: number = 1;
    private pageNoTemplateOption: number = 1;
    private subscribers: Subscription[] = [];
    private geoCoder: any;

    public templates = [
        { label: 'Create New', value: false },
        { label: 'Use existing template', value: true },
    ];

    @Input() public isReadOnlyMode!: boolean;

    @Input()
    set isTouchedForm(status: boolean) {
        if (!this.updateGeneralInformationForm) {
            return;
        }

        setTimeout(() => {
            this.updateGeneralInformationForm.emit(this.form);
        });
    }

    @ViewChild('mapSearch') mapSearchRef!: ElementRef;

    public isCheckUtility: boolean = false;

    constructor(
        private ngZone: NgZone,
        private mapsAPILoader: MapsAPILoader,
        private apiAdapterService: ApiAdapterServiceService,
        private toastr: ToastrService,
        private httpClient: HttpClient
    ) {
        this.updateGeneralInformationForm = new EventEmitter();
    }

    ngOnInit(): void {
        this.msgTypeWAOption = MsgTypeWAOptionConst;
        this.mediaTypeOption = MediaTypeOptionConst;
        this.categoryOption = generateEnumOption(ProductCategoryEnum);
        this.validationMessages = GeneralInformationWAMessageValidationConst;
        this.maps = {
            zoom: 15,
            latitude: null,
            longitude: null,
            isDraggable: true,
            isDoubleClickZoom: true,
        };

        this.initForm();
        this.initDisable();
        this.initEdit();
        this.initValidation();
        this.getAccountOptionService(
            this.getCampaignStorage?.generalInformation?.form?.medium === 'WA'
                ? this.getCampaignStorage?.generalInformation?.form?.from?.name
                : ''
        );

        // Listen text to changes in the form
        const textControlForSync = this.getControl('text');

        if (textControlForSync) {
            const textSubs = textControlForSync.valueChanges.subscribe((value) => {
                this.syncVariablesFromText(value);
            });

            this.subscribers.push(textSubs);
        }
    }

    get getCampaignStorage(): any {
        const campaignData = localStorage.getItem('telin.campaign');
        return campaignData ? JSON.parse(campaignData) : {};
    }

    private initForm(): void {
        this.form = new FormGroup({
            channel: new FormControl(null, Validators.required),
            media: new FormControl(null),
            from: new FormControl(null),
            text: new FormControl(null),
            fileName: new FormControl(null),
            targetURL: new FormControl(null),
            fileType: new FormControl(null),
            file: new FormControl(null),
            latitude: new FormControl(null),
            longitude: new FormControl(null),
            template: new FormControl(null),
            paramHeader: new FormArray([], Validators.required),
            paramBody: new FormArray([], Validators.required),
            paramButton: new FormArray([], Validators.required),
            otpValue: new FormControl(''),
            isUpload: new FormControl(false),
            fileId: new FormControl(''),
            category: new FormControl(null),
            product: new FormControl(null),
            catalogue: new FormControl(null),
            body: new FormControl(null),
            isUseExistingTemplate: new FormControl(false),
            isNeedSave: new FormControl(false),
            ttl: new FormControl(null),
            isDownloadVariableXLS: new FormControl(false),
            variables: new FormArray([]),
            useXlsx: new FormControl(false),
            buttonParams: new FormArray([]),
            paramCarousel: new FormArray([]),
        });

        this.form.valueChanges.subscribe(() => {
            setTimeout(() => {
                this.updateGeneralInformationForm.emit(this.form);
            });
        });
        this.form.get('isUpload')?.valueChanges.subscribe((val) => {
            this.toggleExcelValidation(val);
        });
    }

    private initDisable(): void {
        if (!this.isReadOnlyMode) {
            return;
        }

        this.form.disable();
    }

    private initEdit(): void {
        const storageData = this.getCampaignStorage;
        const formValues = storageData?.generalInformation?.form;
        if (
            !storageData ||
            Object.entries(storageData).length === 0 ||
            formValues?.medium !== 'WA'
        ) {
            return;
        }
        this.getControl('channel')?.setValue(formValues?.channel);
        this.getControl('media')?.setValue(formValues?.media);
        this.getControl('from')?.setValue(formValues?.from);
        this.getControl('text')?.setValue(formValues?.text);
        this.getControl('targetURL')?.setValue(formValues?.targetURL);
        this.getControl('fileName')?.setValue(
            formValues?.fileName ?? this.getControl('targetURL')?.value
        );
        this.getControl('template')?.setValue(formValues?.template);
        this.getControl('isUpload')?.setValue(formValues?.isUpload);
        this.getControl('fileId')?.setValue(formValues?.fileId);
        this.getControl('otpValue')?.setValue(formValues?.otpValue);
        this.getControl('isUseExistingTemplate')?.setValue(
            formValues?.isUseExistingTemplate !== undefined
                ? formValues.isUseExistingTemplate
                : !!formValues?.template?.name
        );
        this.getControl('isNeedSave')?.setValue(!!formValues?.isNeedSave);
        this.getControl('isDownloadVariableXLS')?.setValue(formValues?.isDownloadVariableXLS);
        this.getControl('ttl')?.setValue(formValues?.ttl);

        const savedVariables = formValues?.variables;
        if (savedVariables && Array.isArray(savedVariables) && savedVariables.length > 0) {
            this.variables.clear();

            savedVariables.forEach((item: any) => {
                this.variables.push(
                    new FormGroup({
                        paramName: new FormControl({
                            value: item.paramName || '',
                            disabled: this.isReadOnlyMode,
                        }),
                    })
                );
            });
        }

        this.isHaveTemplateParam = this.getControl('isUpload')?.value;
        this.getTemplateOptionService(null, this.getControl('template')?.value?.name);
    }

    private initValidation(): void {
        if (!this.form) return;

        for (const key in this.form.controls) {
            if (this.form.controls[key]) {
                this.form.controls[key].clearValidators();
                this.form.controls[key].updateValueAndValidity();
            }
        }

        this.getControl('channel')?.setValidators(Validators.required);

        if (this.getIsText) {
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('text')?.setValidators(Validators.required);
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('text')?.updateValueAndValidity();
        } else if (this.getIsMedia) {
            this.getControl('media')?.setValidators(Validators.required);
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('fileName')?.setValidators(Validators.required);
            this.getControl('media')?.updateValueAndValidity();
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('fileName')?.updateValueAndValidity();
        } else if (this.getIsTemplate) {
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('template')?.setValidators(Validators.required);
            this.getControl('paramHeader')?.setValidators(Validators.required);
            this.getControl('paramBody')?.setValidators(Validators.required);
            this.getControl('paramButton')?.setValidators(Validators.required);
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('template')?.updateValueAndValidity();
            this.getControl('paramHeader')?.updateValueAndValidity();
            this.getControl('paramBody')?.updateValueAndValidity();
            this.getControl('paramButton')?.updateValueAndValidity();
        } else if (this.getIsCatalogue) {
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('category')?.setValidators(Validators.required);
            this.getControl('catalogue')?.setValidators(Validators.required);
            this.getControl('product')?.setValidators(Validators.required);
            this.getControl('body')?.setValidators(Validators.maxLength(100));
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('category')?.updateValueAndValidity();
            this.getControl('catalogue')?.updateValueAndValidity();
            this.getControl('product')?.updateValueAndValidity();
            this.getControl('body')?.updateValueAndValidity();
        } else if (this.getIsUtility) {
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('text')?.setValidators([
                Validators.required,
                Validators.maxLength(1024),
            ]);
            this.getControl('ttl')?.setValidators([
                CustomValidators.min(30, 'Minimum TTL 30'),
                CustomValidators.max(43200, 'Maximum TTL 43200'),
            ]);
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('text')?.updateValueAndValidity();
            this.getControl('ttl')?.updateValueAndValidity();
            this.getControl('variables')?.updateValueAndValidity();
        } else {
            this.getControl('from')?.setValidators(Validators.required);
            this.getControl('text')?.setValidators(Validators.required);
            this.getControl('from')?.updateValueAndValidity();
            this.getControl('text')?.updateValueAndValidity();
        }
    }

    public formReset(_isMediaType: boolean = false): void {
        if (!_isMediaType) {
            this.getControl('media')?.reset();
            this.getControl('from')?.reset();
        }

        this.getControl('text')?.reset();
        this.getControl('fileName')?.reset();
        this.getControl('targetURL')?.reset();
        this.getControl('fileType')?.reset();
        this.getControl('file')?.reset();
        this.getControl('latitude')?.reset();
        this.getControl('longitude')?.reset();
        this.getControl('template')?.reset();
        this.getControl('category')?.reset();
        this.getControl('product')?.reset();
        this.getControl('catalogue')?.reset();
        this.getControl('body')?.reset();
        this.getControl('otpValue')?.reset();
        this.getControl('variables')?.reset();

        const paramHeader = this.getControl('paramHeader') as FormArray | null;
        if (paramHeader) {
            paramHeader.clear();
            paramHeader.reset();
        }

        const paramBody = this.getControl('paramBody') as FormArray | null;
        if (paramBody) {
            paramBody.clear();
            paramBody.reset();
        }

        const paramButton = this.getControl('paramButton') as FormArray | null;
        if (paramButton) {
            paramButton.clear();
            paramButton.reset();
        }

        this.initValidation();
    }

    private getControl(controlName: string): AbstractControl | null {
        return this.form ? this.form.get(controlName) : null;
    }

    private initParam(_name: string, _value: any, _type: string): Parameter {
        return {
            name: _name,
            value: _value,
            in: 'query',
            type: _type,
        };
    }

    private getAccountOptionService(filter: string = '', isInfinite: boolean = false): void {
        this.isLoadingAccountOption = true;

        const parameterList: Array<Parameter> = [
            this.initParam('pageNo', this.pageNoAccountOption, 'number'),
            this.initParam('rowPerPage', 20, 'number'),
            this.initParam('platform', 'WA', 'string'),
            this.initParam('status', 'APPROVED', 'string'),
            this.initParam('filter', filter, 'string'),
        ];

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/admin/account', 'GET')
            .subscribe((resp: AppResponse<WhatsAppAccountDto[]>) => {
                if (!resp.data) return;

                this.accountOption = isInfinite ? [...this.accountOption, ...resp.data] : resp.data;
                this.isHaveNextAccountOption = resp.haveNext ?? true;
                this.isLoadingAccountOption = false;
            });

        this.subscribers.push(subs);
    }

    public getTemplateOptionService(
        event: string = null,
        filter: string = '',
        isInfinite: boolean = false
    ): void {
        this.isLoadingTemplateOption = true;

        const parameterList: Array<Parameter> = [
            this.initParam('pageNo', this.pageNoTemplateOption, 'number'),
            this.initParam('rowPerPage', 20, 'number'),
            this.initParam('platform', 'WA', 'string'),
            this.initParam('templateType', 'all', 'string'),
            this.initParam('filter', filter, 'string'),
            this.initParam('status', 'APPROVED', 'string'),
            this.initParam('clientId', this.getControl('from')?.value?.clientId ?? '', 'string'),
            this.initParam('wabaId', this.getControl('from')?.value?.wabaId ?? '', 'string'),
        ];

        if (this.isReadOnlyMode) parameterList.push(this.initParam('summary', true, 'string'));

        const messageType = this.form.value?.channel;
        if (messageType === 'utility') {
            parameterList.push(this.initParam('isDirectSend', true, 'string'));
        } else {
            parameterList.push(this.initParam('isDirectSend', false, 'string'));
        }

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/message/template', 'GET')
            .subscribe((resp: AppResponse<TemplateWAModel[]>) => {
                const currentOptions = this.templateOption || [];
                const newData = resp.data || [];
                this.templateOption = isInfinite ? [...currentOptions, ...newData] : newData;

                this.isHaveNextTemplateOption = resp.haveNext ?? true;
                this.isLoadingTemplateOption = false;

                const template = this.templateOption?.find(
                    (t) => t.ID === this.getControl('template')?.value?.ID
                );

                if (template) {
                    this.getControl('template')?.patchValue(template);

                    if (this.getControl('isUpload')?.value)
                        return this.updateDownloadCSV(this.getControl('isUpload')?.value);
                }
                this.updateTemplate(this.getControl('template')?.value);
            });

        this.subscribers.push(subs);
    }

    private initParameter(isButton: boolean = false, value: string = ''): FormGroup {
        const form = isButton
            ? new FormGroup({
                  name: new FormArray([], Validators.required),
              })
            : new FormGroup({
                  name: new FormControl(
                      {
                          value: value,
                          disabled: this.isReadOnlyMode || ['$UnsubscribeAccount'].includes(value),
                      },
                      Validators.required
                  ),
              });

        return form;
    }

    get getParamHeader(): FormGroup[] {
        return (this.form.get('paramHeader') as FormArray).controls as FormGroup[];
    }

    public addParamHeader(value: string = ''): void {
        const form = this.initParameter(false, value);
        (this.form.get('paramHeader') as FormArray).push(form);
    }

    get getParamBody(): FormGroup[] {
        return (this.form.get('paramBody') as FormArray).controls as FormGroup[];
    }

    public addParamBody(value: string = ''): void {
        const form = this.initParameter(false, value);
        (this.form.get('paramBody') as FormArray).push(form);
    }

    get getButton(): FormGroup[] {
        return (this.form.get('paramButton') as FormArray).controls as FormGroup[];
    }

    public getParamButton(index: number): FormGroup[] {
        const form = (this.form.get('paramButton') as FormArray).controls as FormGroup[];
        return (form[index].get('name') as FormArray).controls as FormGroup[];
    }

    public addParamButton(): void {
        const form = this.initParameter(true);
        (this.form.get('paramButton') as FormArray).push(form);
    }

    get getIsText(): boolean {
        return (
            this.getControl('channel')?.value &&
            this.getControl('channel')?.value?.toLowerCase() === 'text'
        );
    }

    get getIsMedia(): boolean {
        return (
            this.getControl('channel')?.value &&
            this.getControl('channel')?.value?.toLowerCase() === 'media'
        );
    }

    get getIsTemplate(): boolean {
        return (
            this.getControl('channel')?.value &&
            this.getControl('channel')?.value?.toLowerCase() === 'template'
        );
    }

    get getIsCatalogue(): boolean {
        return (
            this.getControl('channel')?.value &&
            this.getControl('channel')?.value?.toLowerCase() === 'catalogue'
        );
    }

    get getIsUtility(): boolean {
        return (
            this.getControl('channel')?.value &&
            this.getControl('channel')?.value?.toLowerCase() === 'utility'
        );
    }

    get getCarouselControls() {
        return (this.form.get('paramCarousel') as FormArray).controls as FormGroup[];
    }

    get hasAnyCarouselVariables(): boolean {
        const carouselArray = this.form.get('paramCarousel') as FormArray;
        if (!carouselArray || carouselArray.length === 0) return false;

        return carouselArray.controls.some((_, cIndex) => this.checkCardHasVariables(cIndex));
    }

    checkCardHasVariables(cIndex: number): boolean {
        const bodyLen = this.getCarouselBodyParams(cIndex)?.controls.length || 0;
        const buttonGroups = (this.form.get('paramCarousel') as FormArray)
            .at(cIndex)
            .get('buttonParams') as FormArray;
        let hasBtnInput = false;

        if (buttonGroups) {
            hasBtnInput = buttonGroups.controls.some(
                (btnGroup) => (btnGroup as FormArray).length > 0
            );
        }

        return bodyLen > 0 || hasBtnInput;
    }

    getCarouselButtons(cardIndex: number): any[] {
        const templateValue = this.form.get('template')?.value;
        return templateValue?.carousel?.[cardIndex]?.buttons || [];
    }

    getCarouselBodyParams(cardIndex: number): FormArray {
        return this.getCarouselControls[cardIndex].get('bodyParams') as FormArray;
    }

    getCarouselBtnParams(cardIndex: number, btnIndex: number): FormArray {
        const btnParamsArray = this.getCarouselControls[cardIndex].get('buttonParams') as FormArray;
        return btnParamsArray.at(btnIndex) as FormArray;
    }

    public getNextAccountBatch() {
        if (!this.isHaveNextAccountOption) return;

        this.pageNoAccountOption += 1;
        this.getAccountOptionService('', true);
    }

    public accountSelectedDisplay(
        attribute1: WhatsAppAccountDto,
        attribute2: WhatsAppAccountDto
    ): string {
        return attribute1 && attribute2 && attribute1.id == attribute2.id
            ? `${attribute1.name} (${attribute1.accountNo})`
            : '';
    }

    public getNextTemplateBatch() {
        if (!this.isHaveNextTemplateOption) return;

        this.pageNoTemplateOption += 1;
        this.getTemplateOptionService(null, '', true);
    }

    public templateSelectedDisplay(
        attribute1: TemplateWAModel,
        attribute2: TemplateWAModel
    ): string {
        return attribute1 && attribute2 && attribute1.name == attribute2.name
            ? `${attribute1.name}`
            : '';
    }

    public onFileSelected(event: Event) {
        const file: FileList = (event.target as HTMLInputElement).files;
        const accept = this.extFileAccepted.split('/');
        let matchType = accept[0];

        if (file.length === 0) return;

        if (this.extFileAccepted === 'application/pdf') matchType = accept[1];

        if (!file[0].type.includes(matchType)) {
            this.toastr.error('Sorry, the file you are uploading is not allowed');
            return;
        }

        if (file[0].size > 10000000) {
            this.toastr.error('Upload media failed, max size 10mb');
            return;
        }

        this.getControl('file')?.setValue(file[0]);
        this.getControl('fileType')?.setValue('image');
        this.getControl('fileName')?.setValue(file[0].name);
        this.getControl('targetURL')?.setValidators(Validators.required);
        this.getControl('targetURL')?.updateValueAndValidity();

        if (file[0].type.indexOf('video') > -1) this.getControl('fileType')?.setValue('video');

        this.uploadMediaService();
    }

    private uploadMediaService(): void {
        const parameterList: Array<Parameter> = [
            {
                name: 'file',
                value: this.getControl('file')?.value,
                in: 'formData',
                type: 'object',
            },
        ];

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/message/media', 'POST')
            .subscribe((resp: AppResponse<any>) => {
                this.getControl('targetURL')?.clearValidators();
                this.getControl('targetURL')?.updateValueAndValidity();
                this.getControl('targetURL')?.setValue(resp.data.targetURL);
                this.getControl('file')?.reset();

                this.toastr.success('Upload media success');
            });

        this.subscribers.push(subs);
    }

    get buttonParamsControls() {
        const control = this.form.get('buttonParams') as FormArray;
        return control ? control.controls : [];
    }

    get isUsingExcelBulk(): boolean {
        return this.getControl('isUpload')?.value || false;
    }

    public updateTemplate(template: TemplateWAModel): void {
        this.isShowParameter = false;

        if (!template) return;

        const formStorage = this.getCampaignStorage?.generalInformation?.form;

        const buttonParamsArray = this.form.get('buttonParams') as FormArray;
        buttonParamsArray.clear();

        if (template && template.buttons && template.buttons.length > 0) {
            let qrIndexLocal = 0;

            template.buttons.forEach((btn: any, index: number) => {
                if (btn.type === 'QUICK_REPLY') {
                    let existingPayload = '';

                    // SKENARIO 1: Data dari Local State
                    if (formStorage?.buttonParams && formStorage.buttonParams.length > 0) {
                        existingPayload = formStorage.buttonParams[qrIndexLocal]?.payload || '';
                        qrIndexLocal++;
                    }
                    // SKENARIO 2: Data dari BE
                    else if (
                        formStorage?.paramButton &&
                        Array.isArray(formStorage.paramButton[index])
                    ) {
                        existingPayload = formStorage.paramButton[index][0] || '';
                    }

                    buttonParamsArray.push(
                        new FormGroup({
                            label: new FormControl(btn.text),
                            payload: new FormControl(
                                {
                                    value: existingPayload,
                                    disabled: this.isReadOnlyMode,
                                },
                                [Validators.maxLength(256)]
                            ),
                        })
                    );
                }
            });
        }

        this.isHaveTemplateParam =
            (template.header?.examples?.length ?? 0) > 0 ||
            (template.body?.examples?.length ?? 0) > 0 ||
            (template.buttons?.length ?? 0) > 0 ||
            (template.carousel?.length ?? 0) > 0;

        if (['image', 'video', 'document'].includes(template?.header?.type?.toLowerCase())) {
            this.getControl('fileName')?.setValue(
                this.getCampaignStorage?.generalInformation?.form.fileName ??
                    this.getCampaignStorage?.generalInformation?.form.targetURL ??
                    template.header.url
            );
            this.getControl('targetURL')?.setValue(
                this.getCampaignStorage?.generalInformation?.form.targetURL ?? template.header.url
            );

            this.fileTypeChange(template?.header?.type?.toLowerCase());
        }

        (template.header.examples?.length ?? 0) > 0
            ? this.getControl('paramHeader')?.setValidators(Validators.required)
            : this.getControl('paramHeader')?.clearValidators();

        (template.body.examples?.length ?? 0) > 0
            ? this.getControl('paramBody')?.setValidators(Validators.required)
            : this.getControl('paramBody')?.clearValidators();

        (template.buttons?.length ?? 0) > 0
            ? this.getControl('paramButton')?.setValidators(Validators.required)
            : this.getControl('paramButton')?.clearValidators();

        (this.form.get('paramHeader') as FormArray).controls = [];
        (this.form.get('paramHeader') as FormArray).reset();
        this.getControl('paramHeader')?.updateValueAndValidity();
        (this.form.get('paramBody') as FormArray).controls = [];
        (this.form.get('paramBody') as FormArray).reset();
        this.getControl('paramBody')?.updateValueAndValidity();
        (this.form.get('paramButton') as FormArray).controls = [];
        (this.form.get('paramButton') as FormArray).reset();
        this.getControl('paramButton')?.updateValueAndValidity();
        this.getControl('otpValue')?.setValue('');
        this.getControl('otpValue')?.clearValidators();
        this.getControl('otpValue')?.updateValueAndValidity();

        if (template.category === 'AUTHENTICATION') {
            this.getControl('otpValue')?.setValue(
                formStorage?.otpValue ??
                    [...(formStorage?.template?.body?.examples ?? [])]?.pop() ??
                    ''
            );

            this.getControl('otpValue')?.setValidators(Validators.required);
            this.getControl('otpValue')?.updateValueAndValidity();
        }

        template.header.examples?.forEach((each, index) => {
            this.addParamHeader(
                formStorage?.paramHeader?.[index]?.name ?? formStorage?.paramHeader?.[index] ?? ''
            );
        });

        template.body.examples?.forEach((each, index) => {
            let paramValue =
                formStorage?.paramBody?.[index]?.name ?? formStorage?.paramBody?.[index] ?? '';

            if (['$UnsubscribeAccount'].includes(each)) paramValue = each;

            this.addParamBody(paramValue);
        });

        // ==========================================================
        // TAMBAHAN: Generate Variable Dinamis dari template.body.examples
        // ==========================================================
        this.variables.clear();
        if (template.body?.examples && template.body.examples.length > 0) {
            const isSavedTemplateMatches = formStorage?.template?.name === template.name;
            const isUtilityTemplate = template.category?.toUpperCase() === 'UTILITY';
            template.body.examples.forEach((exampleValue: string, index: number) => {
                let savedVariable = '';
                if (isSavedTemplateMatches) {
                    savedVariable =
                        formStorage?.variables?.[index]?.paramName ??
                        formStorage?.template?.data?.[index];
                }

                const finalValue = savedVariable ? savedVariable : '';

                this.variables.push(
                    new FormGroup({
                        paramName: new FormControl(
                            {
                                value: finalValue,
                                disabled: this.isReadOnlyMode,
                            },
                            isUtilityTemplate ? Validators.required : null
                        ),
                    })
                );
            });
        }

        if (template.body?.text) {
            this.getControl('text')?.setValue(template.body.text);
            this.getControl('text')?.updateValueAndValidity();
        }
        // ==========================================================

        // ==========================================================
        // TAMBAHAN: Generate Form Carousel Dinamis
        // ==========================================================
        const carouselArray = this.form.get('paramCarousel') as FormArray;
        carouselArray.clear();

        const isUsingUpload = this.getControl('isUpload')?.value;
        const dynamicValidator = isUsingUpload ? null : Validators.required;

        if (template.carousel && template.carousel.length > 0) {
            const isSavedTemplateMatches = formStorage?.template?.name === template.name;

            const savedUserInputs = isSavedTemplateMatches ? formStorage?.paramCarousel : null;

            template.carousel.forEach((card: any, cardIdx: number) => {
                const cardGroup = new FormGroup({
                    headerURL: new FormControl(card.header?.url ?? ''),
                    bodyParams: new FormArray([]),
                    buttonParams: new FormArray([]),
                });

                // 1. Setup Body Params
                if (card.body?.examples?.length > 0) {
                    card.body.examples.forEach((_: any, bodyIdx: number) => {
                        // AMBIL VALUE DARI INPUTAN SEBELUMNYA
                        const savedBodyVal =
                            savedUserInputs?.[cardIdx]?.bodyParams?.[bodyIdx]?.paramName ?? '';
                        (cardGroup.get('bodyParams') as FormArray).push(
                            new FormGroup({
                                paramName: new FormControl(
                                    { value: savedBodyVal, disabled: this.isReadOnlyMode },
                                    dynamicValidator
                                ),
                            })
                        );
                    });
                }

                if (card.buttons?.length > 0) {
                    card.buttons.forEach((btn: any, btnIdx: number) => {
                        const btnParamArray = new FormArray([]);

                        // SKENARIO 1: Button dengan variabel (contoh URL / Phone dengan {{1}})
                        if (btn.examples?.length > 0) {
                            btn.examples.forEach((_: any, paramIdx: number) => {
                                const savedBtnVal =
                                    savedUserInputs?.[cardIdx]?.buttonParams?.[btnIdx]?.[paramIdx]
                                        ?.paramName ?? '';

                                btnParamArray.push(
                                    new FormGroup({
                                        paramName: new FormControl(
                                            { value: savedBtnVal, disabled: this.isReadOnlyMode },
                                            dynamicValidator
                                        ),
                                    })
                                );
                            });
                        }
                        // SKENARIO 2: Button bertipe QUICK_REPLY (Butuh input Payload statis)
                        else if (btn.type === 'QUICK_REPLY') {
                            const savedPayloadVal =
                                savedUserInputs?.[cardIdx]?.buttonParams?.[btnIdx]?.[0]?.payload ??
                                '';

                            btnParamArray.push(
                                new FormGroup({
                                    payload: new FormControl(
                                        { value: savedPayloadVal, disabled: this.isReadOnlyMode },
                                        [
                                            Validators.maxLength(256),
                                            ...(isUsingUpload ? [] : [Validators.required]),
                                        ]
                                    ),
                                })
                            );
                        }

                        (cardGroup.get('buttonParams') as FormArray).push(btnParamArray);
                    });
                }

                carouselArray.push(cardGroup);
            });
        }

        template.buttons?.forEach((each, index) => {
            this.addParamButton();
            const button = this.getButton;

            if (each.examples?.length > 0) {
                each.examples.forEach((_, paramIndex) => {
                    let form = null;

                    if (each.type === 'FLOW') {
                        form = this.initParameter(false, each.flow_id);
                    } else {
                        this.isShowParameter = true;
                        form = this.initParameter(
                            false,
                            formStorage?.paramButton?.[index]?.name?.[paramIndex]?.name ??
                                formStorage?.paramButton?.[index]?.name?.[paramIndex] ??
                                formStorage?.paramButton?.[index]?.[paramIndex] ??
                                ''
                        );
                    }

                    (button[button.length - 1].get('name') as FormArray).push(form);
                });
            } else {
                const lastControl = button[button.length - 1]?.get('name');
                lastControl?.clearValidators();
                lastControl?.updateValueAndValidity();
            }
        });

        const paramBodyArray = this.form.get('paramBody') as FormArray;

        paramBodyArray.clearValidators();
        paramBodyArray.controls.forEach((ctrl) => {
            ctrl.get('name')?.clearValidators();
            ctrl.get('name')?.updateValueAndValidity();
        });

        paramBodyArray.updateValueAndValidity();
        this.toggleExcelValidation(this.form.get('isUpload')?.value);
    }

    public updateMediaType(_mediaType: string): void {
        if (_mediaType === EnumData.MEDIA_TYPE_LOCATION) this.initMaps;

        this.formReset(true);
        this.fileTypeChange(_mediaType.toLowerCase());
    }

    private fileTypeChange(type: string): void {
        this.extFileAccepted = 'image/*';

        if (type === EnumData.MEDIA_TYPE_VIDEO.toLowerCase()) {
            this.extFileAccepted = 'video/*';
        } else if (type === EnumData.MEDIA_TYPE_DOCUMENT.toLowerCase()) {
            this.extFileAccepted = 'application/pdf';
        }
    }

    public updateSearch(searchValue: string): void {
        this.pageNoTemplateOption = 1;
        this.getTemplateOptionService(null, searchValue);
    }

    public updateSearchAccount(searchValue: string): void {
        this.pageNoAccountOption = 1;
        this.getAccountOptionService(searchValue);
    }

    private initMaps(): void {
        this.mapsAPILoader.load().then(() => {
            this.setCurrentLocation();

            this.geoCoder = new google.maps.Geocoder();
            const autocomplete = new google.maps.places.Autocomplete(
                this.mapSearchRef.nativeElement
            );
            autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                    const place: google.maps.places.PlaceResult = autocomplete.getPlace();

                    if (!place.geometry) return;

                    this.maps.latitude = place.geometry.location.lat();
                    this.maps.longitude = place.geometry.location.lng();
                    this.getControl('latitude')?.setValue(this.maps.latitude);
                    this.getControl('longitude')?.setValue(this.maps.longitude);
                });
            });
        });
    }

    private setCurrentLocation() {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                if (!this.getControl('longitude')?.value && !this.getControl('latitude')?.value) {
                    this.maps.latitude = position.coords.latitude;
                    this.maps.longitude = position.coords.longitude;
                    this.getControl('latitude')?.setValue(this.maps.latitude);
                    this.getControl('longitude')?.setValue(this.maps.longitude);
                }
            });
        }
    }

    public markerDragEnd(event: google.maps.MouseEvent) {
        if (event.latLng) {
            this.maps.latitude = event.latLng.lat();
            this.maps.longitude = event.latLng.lng();
            this.getControl('latitude')?.setValue(this.maps.latitude);
            this.getControl('longitude')?.setValue(this.maps.longitude);
        }
    }

    public updateDownloadCSV(isChecked: boolean): void {
        this.getControl('isUpload')?.setValue(isChecked);

        if (isChecked) {
            (this.getControl('paramHeader') as FormArray).controls = [];
            (this.getControl('paramHeader') as FormArray).reset();
            (this.getControl('paramBody') as FormArray).controls = [];
            (this.getControl('paramBody') as FormArray).reset();
            (this.getControl('paramButton') as FormArray).controls = [];
            (this.getControl('paramButton') as FormArray).reset();
            this.getControl('paramHeader')?.clearValidators();
            this.getControl('paramBody')?.clearValidators();
            this.getControl('paramButton')?.clearValidators();
            this.getControl('paramHeader')?.updateValueAndValidity();
            this.getControl('paramBody')?.updateValueAndValidity();
            this.getControl('paramButton')?.updateValueAndValidity();
            this.getControl('otpValue')?.reset();
            this.getControl('otpValue')?.clearValidators();
            this.getControl('otpValue')?.updateValueAndValidity();
            this.variables.controls.forEach((ctrl) => {
                ctrl.get('paramName')?.clearValidators();
                ctrl.get('paramName')?.updateValueAndValidity();
            });
            const carouselArray = this.form.get('paramCarousel') as FormArray;
            if (carouselArray) {
                carouselArray.controls.forEach((card: any) => {
                    const bodyParams = card.get('bodyParams') as FormArray;
                    bodyParams?.controls.forEach((bCtrl) => {
                        bCtrl.get('paramName')?.clearValidators();
                        bCtrl.get('paramName')?.updateValueAndValidity();
                    });

                    const btnParams = card.get('buttonParams') as FormArray;
                    btnParams?.controls.forEach((btnGroup: any) => {
                        (btnGroup as FormArray).controls.forEach((pBtnCtrl) => {
                            pBtnCtrl.get('paramName')?.clearValidators();
                            pBtnCtrl.get('paramName')?.updateValueAndValidity();
                            pBtnCtrl.get('payload')?.clearValidators();
                            pBtnCtrl.get('payload')?.updateValueAndValidity();
                        });
                    });
                });
            }
            return;
        }
        this.updateTemplate(this.getControl('template')?.value);
    }

    public handleChangeTemplate(useExisting: boolean): void {
        this.getControl('isDownloadVariableXLS')?.setValue(false);
        this.variables.clear();
        this.updateVariableDownloadXLS(false);
        this.getControl('text')?.setValue('');
        if (useExisting) {
            this.getControl('template')?.setValidators(Validators.required);
            this.getControl('text')?.clearValidators();
            this.getControl('text')?.updateValueAndValidity();
            this.getControl('template')?.updateValueAndValidity();
            const fromValue = this.getControl('from')?.value;
            if (fromValue) {
                this.getTemplateOptionService(fromValue);
            }
        } else {
            this.getControl('template')?.reset();
            this.getControl('template')?.clearValidators();
            this.getControl('text')?.setValidators([Validators.required, Validators.max(1024)]);

            this.getControl('text')?.updateValueAndValidity();
            this.getControl('template')?.updateValueAndValidity();
        }
    }

    public handleChangeFrom(value: any): void {
        const { isUseExistingTemplate } = this.form.value;
        if (!isUseExistingTemplate) return;
        this.getTemplateOptionService(value);
    }

    get variables(): FormArray {
        return this.getControl('variables') as FormArray;
    }

    private syncVariablesFromText(text: string): void {
        if (text === null || text === undefined) return;

        let counter = 1;
        const normalizedText = text.replace(/\{\{\s*\d+\s*\}\}/g, () => {
            const replacement = `{{${counter}}}`;
            counter++;
            return replacement;
        });

        const targetVariableCount = counter - 1;
        if (text !== normalizedText) {
            this.getControl('text')?.setValue(normalizedText, { emitEvent: false });
        }
        const currentCount = this.variables.length;

        if (targetVariableCount > currentCount) {
            const diff = targetVariableCount - currentCount;
            for (let i = 0; i < diff; i++) {
                this.variables.push(
                    new FormGroup({
                        paramName: new FormControl(
                            { value: '', disabled: this.isReadOnlyMode },
                            Validators.required
                        ),
                    })
                );
            }
        } else if (targetVariableCount < currentCount) {
            const diff = currentCount - targetVariableCount;
            for (let i = 0; i < diff; i++) {
                this.variables.removeAt(this.variables.length - 1);
            }
        }
    }

    addVariable(): void {
        const textControl = this.getControl('text');

        if (textControl) {
            const currentBody = textControl.value || '';

            const nextSequence = this.variables.length + 1;
            const newSequence = `{{${nextSequence}}}`;

            textControl.setValue(`${currentBody}${newSequence}`);
        }
    }

    removeVariable(index: number): void {
        this.variables.removeAt(index);

        if (this.variables.controls.length === 0) {
            this.getControl('useXlsx')?.setValue(false);
        }

        const textControl = this.getControl('text');

        if (textControl && textControl.value) {
            const targetSequence = index + 1;
            let currentBody = textControl.value;

            const removeRegex = new RegExp(`\\s*\\{\\{${targetSequence}\\}\\}`, 'g');
            currentBody = currentBody.replace(removeRegex, '');

            currentBody = currentBody.replace(/\{\{(\d+)\}\}/g, (match: number, numStr: string) => {
                const num = parseInt(numStr, 10);
                if (num > targetSequence) {
                    return `{{${num - 1}}}`;
                }
                return match;
            });

            textControl.setValue(currentBody.trim());
        }
    }

    updateVariableDownloadXLS(isChecked: boolean): void {
        const variablesArray = this.variables;
        if (isChecked) {
            variablesArray.controls.forEach((control) => {
                control.get('paramName')?.clearValidators();
                control.get('paramName')?.setValue('');
                control.get('paramName')?.updateValueAndValidity();
            });
        } else {
            variablesArray.controls.forEach((control) => {
                control.get('paramName')?.setValidators(Validators.required);
                control.get('paramName')?.updateValueAndValidity();
            });
        }

        this.form.updateValueAndValidity();
    }

    public toggleExcelValidation(isUpload: boolean): void {
        const currentValidator = isUpload ? null : Validators.required;

        // ====================================================
        // 2. UPDATE VALIDASI CAROUSEL VARIABLES
        // ====================================================
        const carouselArray = this.form.get('paramCarousel') as FormArray;
        if (carouselArray) {
            carouselArray.controls.forEach((card: any) => {
                const bodyParams = card.get('bodyParams') as FormArray;
                bodyParams?.controls.forEach((bCtrl: any) => {
                    bCtrl.get('paramName')?.setValidators(currentValidator);
                    bCtrl.get('paramName')?.updateValueAndValidity({ emitEvent: false });
                });

                const btnParams = card.get('buttonParams') as FormArray;
                btnParams?.controls.forEach((btnGroup: any) => {
                    (btnGroup as FormArray).controls.forEach((pBtnCtrl: any) => {
                        pBtnCtrl.get('paramName')?.setValidators(currentValidator);
                        pBtnCtrl.get('paramName')?.updateValueAndValidity({ emitEvent: false });
                    });
                });
            });

            carouselArray.updateValueAndValidity({ emitEvent: false });
        }
        this.form.updateValueAndValidity();
    }

    ngOnDestroy(): void {
        this.subscribers.forEach((each) => each.unsubscribe());
    }
}
